import { type FC, useCallback, useEffect, useRef, useState } from "react";

import {
  FormProvider,
  type UseFormReturn,
  useFormContext,
} from "react-hook-form";
import { useLocation } from "react-router-dom";

import { ButtonUI } from "../../../components/Button";
import { FormGroupUI } from "../../../components/FormGroup";
import { FormGroupLabelUI } from "../../../components/FormGroupLabel";
import { InputUI } from "../../../components/rhfs/Input";
import { RadioUI } from "../../../components/rhfs/Radio";
import { ReactSelectUI } from "../../../components/rhfs/ReactSelect";
import { TextareaUI } from "../../../components/rhfs/Textarea";
import { ToggleUI } from "../../../components/rhfs/Toggle";
import { ValidatableDateTimeUI } from "../../../components/rhfs/ValidatableDateTime";
import {
  type FromOptionValue,
  fromOptions,
} from "../../../constants/email-senders";
import {
  type EmailTemplate,
  EmailTemplateCategory,
  type EmailTemplateDocuments,
} from "../../../hooks/useEmailTemplatesRestApi";
import type { OptionItem } from "../../../types/common";

import { ReactComponent as ArrowPath } from "./arrow-path.svg";
import { EmailTemplateEditorModal } from "./components/EmailTemplateEditorModal";
import { EmailTemplateSelectModal } from "./components/EmailTemplateSelectModal";
import type { FormData } from "./index";
import { ReactComponent as QuestionMarkCircle } from "./question-mark-circle.svg";

const COMPANY_PLACEHOLDER = "{{COMPANY}}";
const NAME_PLACEHOLDER = "{{NAME}}";

export type FormValues = {
  category: EmailTemplateCategory;
  from: FromOptionValue | "";
  subject: string;
  content: string;
  hrHash: string;
  scheduled: boolean;
  scheduledAt: string;
  includedMe: boolean;
};

export const radioOptions: OptionItem[] = [
  { value: EmailTemplateCategory.Hr, label: "人材" },
  { value: EmailTemplateCategory.Matter, label: "案件" },
  { value: EmailTemplateCategory.All, label: "人材と案件" },
];

// Propsインターフェースにテンプレート編集用のpropsを追加
interface SendBulkEmailsFormProps {
  hrHash: string;
  scheduled: boolean;
  onGenerateHash: () => void;
  data: { emailTemplates: EmailTemplateDocuments } | undefined;
  // テンプレート編集モーダル用のprops
  templateFormMethods: UseFormReturn<FormData>;
  isTemplateSubmitting: boolean;
  templateErrorMessage: string;
  onTemplateSubmit: (data: FormData) => Promise<boolean>;
  clearTemplateMessages: () => void;
}

// propsを受け取る
export const SendBulkEmailsForm: FC<SendBulkEmailsFormProps> = ({
  hrHash,
  scheduled,
  onGenerateHash,
  data,
  // ropsを分割代入
  templateFormMethods,
  isTemplateSubmitting,
  templateErrorMessage,
  onTemplateSubmit,
  clearTemplateMessages,
}) => {
  const { register, setValue, watch } = useFormContext<FormValues>();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const template = data?.emailTemplates.items[0] ?? null;
    // 「再編集」ボタンから画面遷移の場合は、テンプレートの自動設定をスキップ
    const isFromEdit = !!location.state?.isFromEdit;

    if (template && !isFromEdit) {
      setValue("category", template.category as EmailTemplateCategory);
      setValue("from", template.from as FromOptionValue);
      setValue("subject", template.subject);
      setValue("content", template.body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, location.state, setValue]);

  const handleInsertText = useCallback(
    (text: string) => {
      const textarea = textareaRef.current;
      if (textarea) {
        const {
          selectionStart: startPos,
          selectionEnd: endPos,
          value: currentContent,
        } = textarea;
        const newText = `${currentContent.substring(
          0,
          startPos,
        )}${text}${currentContent.substring(endPos)}`;
        setValue("content", newText, { shouldValidate: true });
        setTimeout(() => {
          const newPos = startPos + text.length;
          textarea.setSelectionRange(newPos, newPos);
          textarea.focus();
        }, 0);
      }
    },
    [setValue],
  );

  const handleTemplateSelect = useCallback(
    (template: EmailTemplate) => {
      setValue("category", template.category as EmailTemplateCategory);
      setValue("from", template.from as FromOptionValue);
      setValue("subject", template.subject);
      setValue("content", template.body);
      setIsTemplateModalOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue],
  );

  const includedMe = watch("includedMe");

  return (
    <form className="relative mb-8">
      <FormGroupUI className="mb-3 md:items-start relative w-full">
        <FormGroupLabelUI className="w-[3.9rem]"></FormGroupLabelUI>
        <div className="flex-grow">
          <ButtonUI
            className="w-40"
            type="button"
            color="info"
            size="sm"
            isOutline={true}
            onClick={() => setIsTemplateModalOpen(true)}
          >
            テンプレート選択
          </ButtonUI>
          <ButtonUI
            className="w-48 ml-2"
            type="button"
            color="info"
            size="sm"
            isOutline={true}
            onClick={() => setIsTemplateEditorOpen(true)}
          >
            テンプレート作成
          </ButtonUI>
        </div>
      </FormGroupUI>
      <FormGroupUI className="mb-3">
        <FormGroupLabelUI className="w-[3.9rem]">配信種別</FormGroupLabelUI>
        <RadioUI
          registerName="category"
          options={radioOptions}
          className="space-x-6"
        />
      </FormGroupUI>
      <FormGroupUI className="mb-3">
        <FormGroupLabelUI className="w-[3.9rem]">配信者</FormGroupLabelUI>
        <div className="flex-grow">
          <ReactSelectUI
            registerName="from"
            options={[
              { value: "", label: "配信者を選択してください" },
              ...fromOptions,
            ]}
            placeholder="配信者を選択してください"
            className="w-72 h-auto py-2"
          />
        </div>
      </FormGroupUI>
      <FormGroupUI className="my-5">
        <FormGroupLabelUI style={{ marginRight: "1rem" }}>
          確認用メール
        </FormGroupLabelUI>
        <div className="flex items-center">
          <ToggleUI
            className="override-toggle"
            color="primary"
            registerName="includedMe"
            defaultChecked={false}
          />
          {includedMe && (
            <p className="text-sm text-green-600 ml-2">自身に送る</p>
          )}
        </div>
      </FormGroupUI>
      <FormGroupUI className="mb-5">
        <FormGroupLabelUI className="w-[3.9rem]">件名</FormGroupLabelUI>
        <InputUI
          registerName="subject"
          className="w-full"
          placeholder="件名を入力してください"
        />
      </FormGroupUI>
      <FormGroupUI className="mb-3 md:items-start relative w-full">
        <FormGroupLabelUI className="w-[3.9rem]"></FormGroupLabelUI>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <ButtonUI
              className="w-40"
              type="button"
              color="success"
              size="sm"
              isOutline={true}
              onClick={() => handleInsertText(COMPANY_PLACEHOLDER)}
            >
              会社変数
            </ButtonUI>
            <ButtonUI
              className="w-40"
              type="button"
              color="success"
              size="sm"
              isOutline={true}
              onClick={() => handleInsertText(NAME_PLACEHOLDER)}
            >
              担当者変数
            </ButtonUI>

            <div
              className="tooltip tooltip-top before:max-w-xs before:content-[attr(data-tip)] before:text-left before:whitespace-pre-wrap"
              data-tip={`変数とは「一時的なラベル」です。
  たとえば{{NAME}}というラベルを文章に含めておくと
  一括配信時に、ラベルが宛先の各担当者名に置き換わります。`}
            >
              <div className="flex items-center cursor-pointer">
                <QuestionMarkCircle className="w-5 h-5 text-gray-600" />
                <span className="text-xs font-semibold">変数とは</span>
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-2 font-bold">
              人材Hash:
            </span>
            <span className="bg-gray-100 px-3 py-2 rounded-md text-gray-700 font-mono text-sm">
              {hrHash}
            </span>
            <ArrowPath
              className="w-5 h-5 ml-2 text-success hover:text-green-400"
              onClick={onGenerateHash}
            />
            <div
              className="tooltip tooltip-top before:max-w-xs before:content-[attr(data-tip)] before:text-left before:whitespace-pre-wrap"
              data-tip={`人材Hashとは、配信メールを追跡するための識別子です。これにより、各メールの開封率やクリック率を分析し、効果測定や改善に役立てます。`}
            >
              <div className="flex items-center cursor-pointer ml-2 mr-2">
                <QuestionMarkCircle className="w-5 h-5 text-gray-600" />
                <span className="text-xs font-semibold">人材Hashとは</span>
              </div>
            </div>
          </div>
        </div>
      </FormGroupUI>
      <FormGroupUI className="mb-3 md:items-start relative w-full">
        <FormGroupLabelUI className="w-[3.9rem]">本文</FormGroupLabelUI>
        <div className="flex flex-col w-full">
          <TextareaUI
            registerName="content"
            textareaRef={textareaRef}
            placeholder="本文を入力してください"
            rows={20}
            className="w-full"
          />
        </div>
      </FormGroupUI>
      <FormGroupUI className="my-5">
        <FormGroupLabelUI>予約配信</FormGroupLabelUI>
        <div className="flex flex-col">
          <ToggleUI color="primary" registerName="scheduled" />
        </div>
        {scheduled && (
          <ValidatableDateTimeUI rules={{}} registerName="scheduledAt" />
        )}
      </FormGroupUI>

      {isTemplateModalOpen && (
        <EmailTemplateSelectModal
          onClose={() => setIsTemplateModalOpen(false)}
          onSelect={handleTemplateSelect}
        />
      )}

      {isTemplateEditorOpen && (
        <FormProvider {...templateFormMethods}>
          <EmailTemplateEditorModal
            onClose={() => setIsTemplateEditorOpen(false)}
            isSubmitting={isTemplateSubmitting}
            errorMessage={templateErrorMessage}
            onSubmit={onTemplateSubmit}
            clearMessages={clearTemplateMessages}
          />
        </FormProvider>
      )}
    </form>
  );
};
